// LN Pro · the source-agnostic ACTIONS contract
//
// PKT-0633. LN Pro already has a working attended-actions queue; it is just
// called Reddit. This is that queue's shape with the word "reddit" taken out of
// it, so Instagram can be adapter #2 without a second surface to rot.
//
// 🔴 WHAT THIS FILE DELIBERATELY DOES NOT DO — read before "finishing" it.
// It does not touch RedditAnswerDesk.tsx or redditAnswerService.ts. The Reddit
// tests mock the module path `../../../services/redditAnswerService` and assert
// literal wire values (`needs_reply`, `copied`), literal labels ("Suggested
// reply", "Copy reply") and the exact action body. Those tests must pass
// UNCHANGED (proof_required 1), so Reddit's wire contract is treated here as
// FIXED VOCABULARY that adapters translate into, never as something to rename.
// The packet's stop_if is explicit: a working surface is not spent to buy a
// cleaner abstraction.

/** Reddit is adapter #1 and already ships. Instagram is adapter #2. */
export type ActionSource = "reddit" | "instagram";

/**
 * Generic queue states. Reddit's wire values are reply-centric because Reddit's
 * action IS a reply; Instagram's is not. The mapping lives in the adapters so
 * neither source has to pretend to be the other.
 *
 *   generic        reddit wire      instagram wire
 *   needs_action   needs_reply      needs_action
 *   done           replied          done
 *   dismissed      dismissed        dismissed
 */
export type ActionQueueState = "needs_action" | "done" | "dismissed" | "all";

/**
 * What the operator did. `copied` only makes sense where there is text to copy;
 * a "go like this post" card has none, which is why suggested_text below is
 * nullable and why the primary verb is per-adapter rather than global.
 */
export type ActionVerb = "copied" | "marked_done" | "dismissed";

/**
 * A claim about the graph, carried onto the card WITH its verification state.
 *
 * `state` is PKT-0632's three-valued edge. It is not a boolean and must not be
 * narrowed into one: `unknown` means the roster was too partial to decide, and
 * collapsing it to false is the exact "unsafe non-follower claim" that packet
 * was written against.
 */
export interface EdgeEvidence {
  kind: "influencer_follows_locatenow" | "candidate_engaged_influencer";
  subject: string;
  state: "verified" | "refuted" | "unknown";
  /** Human-readable justification, including the coverage it rests on. */
  because: string;
}

export interface ActionCard {
  id: string;
  source: ActionSource;
  /** Where the operator goes to act. Never an endpoint we post to. */
  source_url: string;
  score: number;
  verdict: string;
  title: string;
  body_preview: string | null;
  /** The "why:" slot the Reddit card already renders. */
  why: string | null;
  /**
   * NULL where the action carries no text. Grant's example — "go like one of
   * their posts" — has nothing to draft, and an empty "Suggested reply" box
   * with a "Copy reply" button under it is a worse card than no box at all.
   */
  suggested_text: string | null;
  disclosure_link: string | null;
  evidence: EdgeEvidence[];
  /**
   * 🔴 THE TYPE ITSELF FORBIDS AN EXECUTOR. Literal `false`, inherited from
   * RedditQueue.posting_enabled. Meta enforces against automated engagement and
   * the standing design is human-acts-system-records, so "the queue can post"
   * is not a runtime flag that could be flipped — it is unrepresentable.
   */
  posting_enabled: false;
}

export interface ActionQueue {
  items: ActionCard[];
  counts: Record<ActionQueueState, number>;
  posting_enabled: false;
}

/**
 * Social proof, counting ONLY what has been verified.
 *
 * proof_required (2): a card whose influencer→LocateNow edge is `unknown` must
 * render UNKNOWN and must NOT be scored as if verified. So `unknown` edges
 * contribute nothing to the score and are reported separately — a caller
 * cannot receive a score without also receiving the count of things that did
 * not go into it.
 */
export function socialProofEvidence(card: ActionCard): {
  verified: EdgeEvidence[];
  unknown: EdgeEvidence[];
  refuted: EdgeEvidence[];
  /** Number of edges that actually support the card. Never includes unknown. */
  supportingCount: number;
  /** True when the card leans on at least one edge nobody has verified. */
  hasUnverifiedClaim: boolean;
  summary: string;
} {
  const verified = card.evidence.filter((e) => e.state === "verified");
  const unknown = card.evidence.filter((e) => e.state === "unknown");
  const refuted = card.evidence.filter((e) => e.state === "refuted");
  return {
    verified,
    unknown,
    refuted,
    supportingCount: verified.length,
    hasUnverifiedClaim: unknown.length > 0,
    summary: unknown.length
      ? `${verified.length} verified · ${unknown.length} UNKNOWN — the unknown edges are not evidence and are not in the score`
      : `${verified.length} verified`,
  };
}

/**
 * The label the card must render for an edge. `unknown` gets a word, not a
 * blank: an absent chip reads as "nothing to say here", which is how an
 * unverified claim quietly becomes an assumed one.
 */
export function edgeLabel(edge: EdgeEvidence): string {
  switch (edge.state) {
    case "verified":
      return `✓ @${edge.subject} follows LocateNow`;
    case "refuted":
      return `✗ @${edge.subject} does not follow LocateNow`;
    case "unknown":
      return `? @${edge.subject} — follow UNKNOWN, not verified`;
  }
}
